import React from 'react';
import { Factory, Cog, ArrowRight } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import type { PageType } from './Header';

type ManufacturingCapabilitiesProps = {
  onNavigate: (page: PageType) => void;
};

const cards = [
  {
    title: '7 Separate Production Bays with 250 Tonne Handling Capacity',
    subtitle: '5,430 sq.m Area | 80-Tonne Single Assemblies',
    icon: Factory,
    images: ['/manufacturing%20capability%201.png', '/manufacturing%20capability%201.1.png'],
  },
  {
    title: 'Advanced Precision Machines and Robotic Welding',
    subtitle: 'Boring, Milling Machine and CNC Profile Cutting | Custom Lifting Equipment',
    icon: Cog,
    images: ['/manufacturing%20capability%202.png', '/manufacturing%20capability%202.1.png'],
  },
  {
    title: 'From Concept to Commissioning',
    subtitle: 'Engineering and Design | Integrated Manufacturing | Testing and Commissioning',
    icon: ArrowRight,
    images: ['/manufacturing%20capability%203.png', '/manufacturing%20capability%203.1.png'],
  },
];

export const ManufacturingCapabilities: React.FC<ManufacturingCapabilitiesProps> = ({ onNavigate }) => {
  const { isDark } = useTheme();

  return (
    <section className={`w-full py-16 md:py-24 ${isDark ? 'bg-vmil-navy' : 'bg-vmil-light'}`}>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-6 md:mb-8">
          <div className={`inline-flex items-center px-3 sm:px-4 py-1.5 sm:py-2 rounded-full border mb-4 ${
            isDark ? 'bg-vmil-blue/10 border-vmil-blue/20' : 'bg-vmil-blue/10 border-vmil-blue/20'
          }`}>
            <span className="text-xs sm:text-sm font-semibold uppercase tracking-wide text-vmil-blue">
              Our Facility
            </span>
          </div>
          <h2 className={`text-2xl sm:text-3xl md:text-4xl font-bold mb-4 ${isDark ? 'text-white' : 'text-vmil-blue'}`}>
            Our Manufacturing Capabilities
          </h2>
          <p className={`text-base sm:text-lg max-w-2xl mx-auto ${isDark ? 'text-white/80' : 'text-vmil-steel'}`}>
            Seven independent bays with 250-tonne handling capacity, equipped with advanced precision machinery, robotic welding, and ISO-certified expertise
          </p>
        </div>
        <div className="grid md:grid-cols-3 gap-6 md:gap-8 mb-12">
          {cards.map((card, index) => {
            const Icon = card.icon;
            return (
              <div
                key={index}
                className={`p-6 sm:p-8 rounded-xl border transition-all duration-300 hover:shadow-lg overflow-hidden ${
                  isDark 
                    ? 'bg-vmil-blue/10 border-vmil-blue/20 hover:border-vmil-blue/30' 
                    : 'bg-white border-gray-200 shadow-sm hover:shadow-xl hover:border-vmil-blue/20'
                }`}
              >
                <div className="grid grid-cols-2 gap-2 mb-4">
                  {card.images.map((img, i) => (
                    <div key={i} className="relative rounded-lg overflow-hidden aspect-[4/3]">
                      <img
                        src={img}
                        alt={`${card.title} - view ${i + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ))}
                </div>
                <div className={`w-12 h-12 rounded-lg flex items-center justify-center mb-3 ${isDark ? 'bg-vmil-blue/20' : 'bg-vmil-blue/10'}`}>
                  <Icon className={`w-6 h-6 ${isDark ? 'text-vmil-blue' : 'text-vmil-blue'}`} />
                </div>
                <h3 className={`text-lg sm:text-xl font-bold mb-2 ${isDark ? 'text-white' : 'text-vmil-blue'}`}>
                  {card.title}
                </h3>
                <p className={`text-sm sm:text-base ${isDark ? 'text-white/75' : 'text-vmil-steel'}`}>
                  {card.subtitle}
                </p>
              </div>
            );
          })}
        </div>
        <div className="text-center">
          <button
            onClick={() => onNavigate('infrastructure')}
            className="group inline-flex items-center gap-2 px-6 sm:px-8 py-3 sm:py-4 rounded-xl bg-vmil-blue text-white font-bold text-sm sm:text-base shadow-lg shadow-vmil-blue/25 transition-all hover:shadow-xl hover:shadow-vmil-blue/30 hover:scale-[1.02]"
          >
            Explore Full Capabilities
            <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 transition-transform group-hover:translate-x-1" />
          </button>
        </div>
      </div>
    </section>
  );
};
